import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { useLanguage } from "../../context/languageContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as EventAPI from "../../api/event";
import * as ReportAPI from "../../api/report";
import { EventItem } from "../../types/api/event.type";
import { ReportItem } from "../../types/api/report.type";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import { Button } from "../../component/form/Button";
import { ReportStatusCard } from "../../component/other/ReportStatusCard";
import {
    buildDateRange,
    countPendingReportsForMember,
    findMemberReportForDay,
    subtractDays,
    toEventItem,
    toReportItem,
    todayDateOnly
} from "../reports/reportOverview.helpers";
import "./Dashboard.css";

const Dashboard: React.FC = () => {
    const { association, associationRole, user } = useAuth();
    const { loading, setLoadingStatus } = useLoading();
    const { t } = useLanguage();
    const nav = useNavigate();

    const [currentEvents, setCurrentEvents] = useState<EventItem[]>([]);
    const [pastEvents, setPastEvents] = useState<EventItem[]>([]);
    const [reports, setReports] = useState<ReportItem[]>([]);

    const associationId = String(association.id);
    const today = useMemo(() => todayDateOnly(), []);
    const isAthleteView = associationRole === "athlete" || associationRole === "parent";
    const withParam = useCallback((template: string, params: Record<string, string | number>) => {
        return Object.entries(params).reduce((result, [key, value]) => result.replace(`{${key}}`, String(value)), template);
    }, []);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadData = useCallback(async () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        const [currentEventsResponse, pastEventsResponse, reportsResponse] = await Promise.all([
            EventAPI.getCurrent(Number(associationId)),
            EventAPI.getRange(oneMonthAgo, yesterday ),
            ReportAPI.list(associationId),
        ]);

        if (!isSuccessCode(currentEventsResponse.error_code)) {
            return handleApiError(currentEventsResponse.request_id, currentEventsResponse.error_code);
        }
        if (!isSuccessCode(pastEventsResponse.error_code)) {
            return handleApiError(pastEventsResponse.request_id, pastEventsResponse.error_code);
        }
        if (!isSuccessCode(reportsResponse.error_code)) {
            return handleApiError(reportsResponse.request_id, reportsResponse.error_code);
        }

        const mappedCurrentEvents = (Array.isArray(currentEventsResponse.data) ? currentEventsResponse.data : [])
            .map((item: Record<string, unknown>) => toEventItem(item))
            .filter((event) => event.id > 0);
        const mappedPastEvents = (Array.isArray(pastEventsResponse.data) ? pastEventsResponse.data : [])
            .map((item: Record<string, unknown>) => toEventItem(item))
            .filter((event) => event.id > 0);
        const mappedReports = (Array.isArray(reportsResponse.data) ? reportsResponse.data : [])
            .map((item: Record<string, unknown>) => toReportItem(item))
            .filter((report) => report.id > 0);

        setCurrentEvents(mappedCurrentEvents);
        setPastEvents(mappedPastEvents);
        setReports(mappedReports);
    }, [associationId, handleApiError]);

    useEffect(() => {
        setLoadingStatus(true);
        loadData().finally(() => setLoadingStatus(false));
    }, [loadData, setLoadingStatus]);

    const oldestVisibleDay = useMemo(() => subtractDays(today, 30), [today]);
    const eventById = useMemo(() => {
        return [...currentEvents, ...pastEvents].reduce((acc, item) => ({ ...acc, [item.id]: item }), {} as Record<number, EventItem>);
    }, [currentEvents, pastEvents]);

    const athleteDraftCards = useMemo(() => {
        return reports
            .filter((report) => report.status === "draft" && report.event_day >= oldestVisibleDay)
            .map((report) => {
                const event = eventById[report.event_id];
                return {
                    key: `draft-${report.id}`,
                    title: event ? event.name : `Event #${report.event_id}`,
                    subtitle: withParam(t("dashboard.savedDraft"), { day: report.event_day }),
                    description: t("dashboard.savedDraftDescription"),
                    actionLabel: t("dashboard.openDraft"),
                    action: () => nav(`/report/create?event_id=${report.event_id}&report_id=${report.id}`),
                };
            });
    }, [eventById, nav, oldestVisibleDay, reports, t, withParam]);

    const athleteMissingCards = useMemo(() => {
        return [...currentEvents, ...pastEvents]
            .flatMap((event) => buildDateRange(event.start_date, event.end_date).map((day) => ({ event, day })))
            .filter((entry) => entry.day <= today && entry.day >= oldestVisibleDay)
            .filter((entry) => {
                const report = findMemberReportForDay(reports, entry.event.id, entry.day, user.id);
                return !report;
            }) 
            .map((entry) => ({
                key: `missing-${entry.event.id}-${entry.day}`,
                title: entry.event.name,
                subtitle: withParam(t("dashboard.notCompiled"), { day: entry.day }),
                description: t("dashboard.notCompiledDescription"),
                actionLabel: t("dashboard.compileNow"),
                action: () => nav(`/report/create?event_id=${entry.event.id}&day=${entry.day}`),
            }));
    }, [currentEvents, nav, oldestVisibleDay, pastEvents, reports, t, today, user.id, withParam]);

    if (loading) {
        return <LoadingScreenLocal />;
    }        

    return (
        <div className="dashboard-screen">
            <h2>{t("dashboard.welcome")} {user.name}</h2>
            {isAthleteView ? (
                <>
                    <p>{withParam(t("dashboard.pendingReportsCurrentEvents"), {
                        count: currentEvents.reduce((acc, event) => acc + countPendingReportsForMember(event, reports, user.id, today), 0),
                    })}</p>
                    <div className="dashboard-cards-grid">
                        {athleteDraftCards.map((card) => (
                            <ReportStatusCard
                                key={card.key}
                                title={card.title}
                                subtitle={card.subtitle}
                                description={card.description}
                                actionLabel={card.actionLabel}
                                onAction={card.action}
                            />
                        ))}
                        {athleteMissingCards.map((card) => (
                            <ReportStatusCard
                                key={card.key}
                                title={card.title}
                                subtitle={card.subtitle}
                                description={card.description}
                                actionLabel={card.actionLabel}
                                onAction={card.action}
                            />
                        ))}
                    </div>
                    {athleteDraftCards.length === 0 && athleteMissingCards.length === 0 && (
                        <p>{t("dashboard.noRecentPending")}</p>
                    )}
                    <Button name={t("dashboard.openReports")} action={() => nav("/reports")} />
                </>
            ) : (
                <>
                    <p>{withParam(t("dashboard.currentEvents"), { count: currentEvents.length })}</p>
                    <p>{withParam(t("dashboard.pastEvents"), { count: pastEvents.length })}</p>
                    <Button name={t("dashboard.openReportsOverview")} action={() => nav("/reports")} />
                </>
            )}
        </div>
    );
};

export default Dashboard;
